import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import InputMask from 'react-input-mask';

import { hexToRGBA } from "../../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  display: flex;
  flex-direction: column;
  
  position: relative;

  padding-left: 0;
  padding-right: 0;
  padding-bottom: 16px;

  ${({ hideDivider }) => hideDivider && css`
    padding-bottom: 0;
  `};
`;

const StyledLabel = styled(Form.Label)`
  display: flex;
  align-items: center;

  font-family: 'RobotoBold';
  font-size: 15px;
  line-height: calc(1.42857rem * (14 / 16));
  color: #000000;

  span {
    color: #790000;
  }

  a {
    color: #b1b1b1;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #244f74;
    }
  }
`;

const StyledControl = styled(Form.Control)`
  min-height: 34px;

  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 4px;
  padding-right: 4px;

  font-family: 'RobotoRegular';
  line-height: 24px;

  color: rgba(0, 0, 0, .75);
  font-size: 15px;
  background-color: #fff;
  border: 1px solid #655555;
  border-radius: 4px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, .1);
  transition: box-shadow .45s, border-color .45s ease-in-out;

  ${({ suffix }) => suffix && css`
    width: 93%;
  `}

  ${({ isValid }) => isValid && css`
    border: 1px solid #198754 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`} !important;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(40 167 69 / 25%) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    border: 1px solid #DC3545 !important;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`} !important;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
    box-shadow: 0 0 0 0.2rem rgb(220 53 69 / 25%) !important;
  `};

  &::placeholder {
    color: ${hexToRGBA('#424242', 0.5)};
  }

  @media ${({ theme }) => theme.devices.tablet} {
    ${({ suffix }) => suffix && css`
      width: 90%;
    `}
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`
  &.processing-feedback,
  &.valid-feedback,
  &.invalid-feedback {
    font-size: 13px;
    line-height: calc(1.42857rem * (13 / 16));
  }

  &.processing-feedback {
    color: ${hexToRGBA('#424242', 0.5)};
  }

  &.valid-feedback {
    color: #198754;
  }

  &.invalid-feedback {
    color: #DC3545
  }
`;

const StyledSuffixContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  min-width: 34px;
  width: 10%;
  padding-top: 22px;
`;

function MaskInput({
  id,
  hint,
  suffix,
  label,
  hideDivider,
  placeholder,
  required,
  pattern,
  mask,
  maxLength,
  defaultInvalidMessage = '',
  validate,
  isValid,
  isInvalid,
  onKeyPress,
  value = '',
  onChange,
  onMouseEnter,
  onMouseLeave,
}) {
  const handleChange = useCallback((event) => {
    validate && validate(event, { required });
    onChange && onChange(event);
  }, [onChange, validate, required]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter && onMouseEnter({ label: label || placeholder, hint });
  }, [onMouseEnter, label, placeholder, hint]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const showMessage = !(isValid && !isInvalid);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup
        controlId={id}
        hideDivider={hideDivider}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <StyledLabel>
          {label}
          {label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <InputMask
          mask={mask}
          maskChar={null}
          value={value}
          onChange={handleChange}
        >
          {(_) => (
            <StyledControl
              type="phone"
              name={id}
              required={required}
              isValid={isValid}
              isInvalid={isInvalid}
              pattern={pattern}
              placeholder={placeholder}
              maxLength={maxLength}
              onKeyPress={onKeyPress}
              data-hj-allow
              suffix={suffix}
            />
          )}
        </InputMask>
        {showMessage && defaultInvalidMessage && (
          <StyledFeedback type="invalid">
            {defaultInvalidMessage}
          </StyledFeedback>
        )}
        {suffix && (
          <StyledSuffixContainer>
            {suffix}
          </StyledSuffixContainer>
        )}
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default MaskInput;
